import React, { useEffect, useRef, useState } from 'react';
import {
  Card,
  Tabs,
  Input,
  Switch,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  message,
  Spin,
  Alert,
  Divider,
} from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  adminService,
  EmailTemplate,
  EmailTemplateVariable,
} from '../../services/adminService';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const STATUSES = ['processing', 'shipped', 'completed', 'cancelled'] as const;
type StatusKey = (typeof STATUSES)[number];

const STATUS_LABELS: Record<StatusKey, string> = {
  processing: 'Processing',
  shipped: 'Shipped',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

interface Draft {
  subject: string;
  bodyHtml: string;
  enabled: boolean;
}

const emptyDraft = (): Draft => ({ subject: '', bodyHtml: '', enabled: true });

const EmailTemplateManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [activeKey, setActiveKey] = useState<StatusKey>('processing');
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [variables, setVariables] = useState<EmailTemplateVariable[]>([]);
  const [previewHtml, setPreviewHtml] = useState('');

  const bodyRef = useRef<any>(null);

  const draft = drafts[activeKey] ?? emptyDraft();

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh the preview whenever the selected status changes (and after load).
  useEffect(() => {
    if (drafts[activeKey]) {
      void refreshPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, drafts[activeKey] !== undefined]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const { templates, variables: vars } = await adminService.getEmailTemplates();
      const byStatus: Record<string, Draft> = {};
      STATUSES.forEach(s => {
        const row: EmailTemplate | undefined = templates.find(t => t.StatusKey === s);
        byStatus[s] = row
          ? { subject: row.Subject, bodyHtml: row.BodyHtml, enabled: row.Enabled }
          : emptyDraft();
      });
      setDrafts(byStatus);
      setVariables(vars);
    } catch (error) {
      console.error('Failed to load email templates:', error);
      message.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const setDraft = (patch: Partial<Draft>) => {
    setDrafts(prev => ({
      ...prev,
      [activeKey]: { ...(prev[activeKey] ?? emptyDraft()), ...patch },
    }));
  };

  const insertToken = (token: string) => {
    const snippet = `{{${token}}}`;
    const ta: HTMLTextAreaElement | undefined = bodyRef.current?.resizableTextArea?.textArea;
    const current = drafts[activeKey]?.bodyHtml ?? '';
    if (ta && typeof ta.selectionStart === 'number') {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const next = current.slice(0, start) + snippet + current.slice(end);
      setDraft({ bodyHtml: next });
      requestAnimationFrame(() => {
        ta.focus();
        const pos = start + snippet.length;
        ta.setSelectionRange(pos, pos);
      });
    } else {
      setDraft({ bodyHtml: current + snippet });
    }
  };

  const refreshPreview = async () => {
    try {
      setPreviewing(true);
      const result = await adminService.previewEmailTemplate(activeKey, {
        subject: draft.subject,
        bodyHtml: draft.bodyHtml,
      });
      setPreviewHtml(result.html);
    } catch (error) {
      console.error('Failed to render preview:', error);
      message.error('Failed to render preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    if (!draft.subject.trim()) {
      message.error('Subject is required');
      return;
    }
    if (!draft.bodyHtml.trim()) {
      message.error('Body is required');
      return;
    }
    try {
      setSaving(true);
      await adminService.updateEmailTemplate(activeKey, {
        subject: draft.subject,
        bodyHtml: draft.bodyHtml,
        enabled: draft.enabled,
      });
      message.success(`${STATUS_LABELS[activeKey]} template saved`);
      await refreshPreview();
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to save template';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Email Templates</Title>
      <Paragraph type="secondary">
        Customise the email sent to customers on each order status change. Use the variable tags to
        insert order details. The branded header and footer are added automatically.
      </Paragraph>

      <Tabs
        activeKey={activeKey}
        onChange={key => setActiveKey(key as StatusKey)}
        items={STATUSES.map(s => ({ key: s, label: STATUS_LABELS[s] }))}
      />

      <Spin spinning={loading}>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Card title={`${STATUS_LABELS[activeKey]} email`}>
              <Space style={{ marginBottom: 16 }}>
                <Text strong>Send this email on status change</Text>
                <Switch
                  checked={draft.enabled}
                  onChange={checked => setDraft({ enabled: checked })}
                />
              </Space>
              {!draft.enabled && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="This email is disabled — advancing an order to this status will not email the customer."
                />
              )}

              <div style={{ marginBottom: 16 }}>
                <Text strong>Subject</Text>
                <Input
                  value={draft.subject}
                  onChange={e => setDraft({ subject: e.target.value })}
                  placeholder="e.g. DasoonEgg — Order {{orderNumber}} has shipped"
                  maxLength={255}
                />
              </div>

              <div style={{ marginBottom: 8 }}>
                <Text strong>Body (HTML)</Text>
              </div>
              <TextArea
                ref={bodyRef}
                value={draft.bodyHtml}
                onChange={e => setDraft({ bodyHtml: e.target.value })}
                rows={14}
                placeholder="<p>Hi {{customerName}},</p> ..."
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />

              <Divider orientation="left" plain style={{ marginTop: 16 }}>
                Variables (click to insert)
              </Divider>
              <Space size={[8, 8]} wrap>
                {variables.map(v => (
                  <Tag
                    key={v.token}
                    color="blue"
                    style={{ cursor: 'pointer' }}
                    title={v.description}
                    onClick={() => insertToken(v.token)}
                  >
                    {`{{${v.token}}}`}
                  </Tag>
                ))}
              </Space>

              <Divider />
              <Space>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={saving}
                  onClick={handleSave}
                >
                  Save
                </Button>
                <Button icon={<ReloadOutlined />} loading={previewing} onClick={refreshPreview}>
                  Update preview
                </Button>
              </Space>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="Preview (sample data)">
              <Spin spinning={previewing}>
                <iframe
                  title="email-preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  style={{ width: '100%', height: 600, border: '1px solid #f0f0f0', borderRadius: 4 }}
                />
              </Spin>
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default EmailTemplateManagement;
